import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FlowPageLayout } from '@/layouts/FlowPageLayout';
import StepIndicator from '@/clients/onboarding/components/StepIndicator';
import BasicInformationStep from '@/clients/onboarding/components/BasicInformationStep';
import HealthMeasurementsStep from '@/clients/onboarding/components/HealthMeasurementsStep';
import InviteOnlyStep, {
  InviteActionsState,
} from '@/clients/onboarding/components/InviteOnlyStep';
import ScheduleAppointmentDialog, {
  type ScheduleAppointmentDraftPayload,
} from '@/components/appointments/ScheduleAppointmentDialog';
import InviteQuestionnaireModal from '@/clients/onboarding/components/InviteQuestionnaireModal';
import CreateInvoiceFlowDialog, { type InvoiceFormDraft } from '@/components/dashboard/CreateInvoiceFlowDialog';
import { createClientInvoice, type CreateInvoicePayload } from '@/lib/api/invoices';
import { createAppointment } from '@/lib/api/appointments';
import { assignQuestionnaire } from '@/lib/api/questionnaires';
import FormulaValidationModal from '@/components/clients/shared/FormulaValidationModal';
import { getClientDetailsPath } from '@/components/clients/ClientNameLink';
import { useClientForm } from '@/clients/onboarding/components/hooks';
import { useFormulas } from '@/clients/onboarding/components/hooks/useFormulas';
import { useCalculation } from '@/clients/onboarding/components/hooks/useCalculation';
import { useDefaultValues } from '@/clients/onboarding/components/hooks/useDefaultValues';
import { useFormulaWarningModal } from '@/clients/onboarding/components/hooks/useFormulaValidation';
import { INITIAL_FORM_DATA, step1Schema } from '@/clients/onboarding/components/constants';
import DemoPersonaPicker from '@/clients/onboarding/components/DemoPersonaPicker';
import {
  buildDemoPersonaFormData,
  DEMO_PERSONAS,
  isCalorieScienceEmail,
  type DemoPersonaId,
} from '@/clients/onboarding/demoPersonas';
import { saveClient } from '@/clients/onboarding/components/utils';
import { getPregnancyLactationValidationErrors } from '@/clients/onboarding/components/pregnancy-lactation-validation';
import { getCountryCodeFromDialCode, getPhoneCountryFromLocation } from '@/utils/country-phone-mapping';
import { buildAppointmentUtcTimes } from '@/utils/appointments/appointment-timezone';
import type { FormData } from '@/clients/onboarding/components/types';
import type { Client, SaveClientDraftPayload } from '@/types/client';
import { getClientDraft, saveOrUpdateClientDraft } from '@/lib/apiService';
import { getCurrentUser } from '@/lib/api/auth';
import {
  trackClientOnboardingBackClicked,
  trackClientOnboardingCompleted,
  trackClientOnboardingExited,
  trackClientOnboardingFollowUpSelected,
  trackClientOnboardingStepCompleted,
  trackClientOnboardingStepViewed,
} from '@/lib/analytics/trackers/clientOnboarding';
import { extractErrorMessage, showErrorToast, showSuccessToast } from '@/utils/toast-utils';

interface AddClientPageLocationState {
  initialValues?: Partial<FormData>;
  returnTo?: string;
  /** Resume an onboarding draft saved server-side (from the Drafts tab). */
  draftClientId?: string;
}

interface AddClientPageProps {
  adminInviteMode?: boolean;
}

type AddClientInviteFlowSource = 'step1_quick' | 'step2_full';
type ClientOnboardingStepName =
  | 'basic_information'
  | 'health_measurements'
  | 'quick_invite_review'
  | 'full_invite_review';

type AddClientOnboardingDraft = {
  version: 1;
  formData: Partial<FormData>;
  currentStep: number;
  isInviteOnlyMode: boolean;
  inviteFlowSource: AddClientInviteFlowSource | null;
  inviteMessage: string;
  inviteActions: InviteActionsState;
  selectedQuestionnaireIds: string[];
  appointmentPayload: ScheduleAppointmentDraftPayload | null;
  pendingInvoiceDraft: CreateInvoicePayload | null;
  pendingInvoiceFormDraft: InvoiceFormDraft | null;
  applyGrowthFactor: boolean;
  createdClientId: string | null;
  updatedAt: number;
};

type DraftPersistenceErrorAction = 'return_to_drafts' | 'view_client';

type DraftPersistenceErrorState = {
  title: string;
  message: string;
  action?: DraftPersistenceErrorAction;
};

const getApiErrorCode = (error: unknown): string | null => {
  if (!axios.isAxiosError(error)) return null;
  const data = error.response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const code = (data as Record<string, unknown>).code;
  return typeof code === 'string' ? code : null;
};

const getDraftPersistenceErrorState = (
  error: unknown,
  fallbackTitle: string
): DraftPersistenceErrorState => {
  const message = extractErrorMessage(error);

  switch (getApiErrorCode(error)) {
    case 'CLIENT_DRAFT_NOT_FOUND':
      return {
        title: 'Draft unavailable',
        message,
        action: 'return_to_drafts',
      };
    case 'CLIENT_DRAFT_NOT_EDITABLE':
      return {
        title: 'Client already submitted',
        message,
        action: 'view_client',
      };
    case 'CLIENT_EMAIL_ALREADY_EXISTS':
      return {
        title: 'Email already exists',
        message,
      };
    case 'CLIENT_PHONE_ALREADY_EXISTS':
      return {
        title: 'Phone already exists',
        message,
      };
    case 'CLIENT_DRAFT_VALIDATION_FAILED':
      return {
        title: 'Check draft details',
        message,
      };
    default:
      return {
        title: fallbackTitle,
        message,
      };
  }
};

const ADD_CLIENT_ONBOARDING_DRAFT_VERSION = 1;

const getAddClientOnboardingDraftStorageKey = (adminInviteMode: boolean) =>
  `caloriescience:${adminInviteMode ? 'admin-client-invite' : 'add-client'}:onboarding-draft:v1`;

const getSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const normalizeInviteActions = (value: unknown): InviteActionsState => {
  const record = value && typeof value === 'object' ? value as Partial<InviteActionsState> : {};
  return {
    scheduleAppointment: record.scheduleAppointment === true,
    attachQuestionnaire: record.attachQuestionnaire === true,
    requestPayment: record.requestPayment === true,
  };
};

const removeAddClientOnboardingDraft = (storageKey: string) => {
  const storage = getSessionStorage();
  if (!storage) return;

  try {
    storage.removeItem(storageKey);
  } catch {
    // Ignore storage failures.
  }
};

const uniqueIds = (ids: string[]): string[] =>
  Array.from(new Set(ids.map((id) => id.trim()).filter(Boolean)));

const normalizeInviteActionName = (action: keyof InviteActionsState): string =>
  action.replace(/([A-Z])/g, '_$1').toLowerCase();

const normalizeOnboardingRoute = (path: string): string => {
  const [pathname, queryString = ''] = path.split('?');
  const normalizedPath = pathname
    .split('/')
    .map((segment) => {
      let decoded = segment || '';
      try {
        decoded = decodeURIComponent(decoded);
      } catch {
        decoded = segment || '';
      }
      if (
        /^\d+$/.test(decoded) ||
        /^[0-9a-f]{16,}$/i.test(decoded) ||
        /^[A-Za-z0-9_-]{18,}$/.test(decoded)
      ) {
        return ':id';
      }
      return segment;
    })
    .join('/');

  const queryKeys = queryString
    ? Array.from(new URLSearchParams(queryString).keys()).sort()
    : [];

  return queryKeys.length > 0 ? `${normalizedPath}?${queryKeys.join('&')}` : normalizedPath;
};

const getOnboardingDestinationSurface = (path: string): string => {
  const route = normalizeOnboardingRoute(path);
  if (route.startsWith('/clients/:id/details-v2') || route.startsWith('/clients/:id')) {
    return 'client_details';
  }
  if (route.startsWith('/clients')) {
    return 'clients_list';
  }
  if (route.startsWith('/admin/client-invites')) {
    return 'admin_client_invites';
  }
  if (route.startsWith('/admin')) {
    return 'admin';
  }
  return 'other';
};

const MINIMUM_ENERGY_THRESHOLD_BY_SEX = {
  male: 1500,
  female: 1200,
} as const;

const WEIGHT_GOAL_ADJUSTMENT_BY_VALUE: Record<string, number> = {
  aggressive_loss: -1000,
  moderate_loss: -500,
  mild_loss: -250,
  standard: -500,
  weight_loss: -500,
  maintain: 0,
  mild_gain: 250,
  moderate_gain: 500,
  aggressive_gain: 750,
};

const toFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const parseAdjustmentValue = (value: string | undefined): number | null => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeGoalKey = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const getMinimumEnergyThreshold = (sex: string): number | null => {
  if (sex === 'male') return MINIMUM_ENERGY_THRESHOLD_BY_SEX.male;
  if (sex === 'female') return MINIMUM_ENERGY_THRESHOLD_BY_SEX.female;
  return null;
};

const getSexMinimumLabel = (sex: string): string =>
  sex === 'male' ? 'male clients' : 'female clients';

const STEP1_SUBMIT_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'phone_country_code',
  'location',
  'cityOrCounty',
  'zipCode',
  'preferredMeasurementSystem',
  'gender',
  'sexAtBirth',
  'dateOfBirth',
  'age',
] as const satisfies ReadonlyArray<keyof FormData>;

const STEP2_SUBMIT_FIELDS = [
  'weightKg',
  'heightCm',
  'activityLevel',
  'feedingType',
  'formulaId',
  'drvFormulaId',
  'waistCircumferenceCm',
  'hipCircumferenceCm',
  'bodyFatPercentage',
  'pregnancyStatus',
  'pregnancyTrimester',
  'prePregnancyWeightKg',
  'lactationStatus',
  'twinPregnancy',
  'weightLossGoal',
  'customCalorieAdjustment',
  'allergies',
  'foodIntolerances',
  'dietaryPreferences',
  'cuisineTypes',
  'medicalConditions',
  'diabetesType',
  'thyroidType',
  'healthGoal',
] as const satisfies ReadonlyArray<keyof FormData>;

const pickSubmitFields = (
  currentValues: FormData,
  formValues: Partial<FormData>,
  fields: ReadonlyArray<keyof FormData>
): Partial<FormData> => {
  return fields.reduce<Partial<FormData>>((picked, field) => {
    const currentValue = formValues[field];
    const fallbackValue = currentValues[field];
    const value = typeof currentValue === 'string' && currentValue.trim()
      ? currentValue
      : String(fallbackValue ?? '');

    if (value.trim()) {
      picked[field] = value;
    }

    return picked;
  }, {});
};

const pickDraftFields = (
  currentValues: FormData,
  formValues: Partial<FormData>,
  fields: ReadonlyArray<keyof FormData>
): Partial<FormData> => {
  return fields.reduce<Partial<FormData>>((picked, field) => {
    const formValue = formValues[field];
    const fallbackValue = currentValues[field];
    const value = formValue !== undefined && formValue !== null
      ? formValue
      : fallbackValue;

    picked[field] = typeof value === 'string' ? value : String(value ?? '');
    return picked;
  }, {});
};

const createOnboardingAppointment = async (
  clientId: string,
  appointmentDraft: ScheduleAppointmentDraftPayload
) => {
  const utcTimes = buildAppointmentUtcTimes({
    date: appointmentDraft.date,
    startTime: appointmentDraft.startTime,
    endTime: appointmentDraft.endTime,
    timezone: appointmentDraft.timezone,
    startTimeUtc: appointmentDraft.startTimeUtc,
    endTimeUtc: appointmentDraft.endTimeUtc,
  });

  if (!utcTimes.startTime || !utcTimes.endTime) {
    throw new Error('Unable to resolve appointment time.');
  }

  const description = appointmentDraft.description?.trim();
  await createAppointment({
    clientId,
    title: appointmentDraft.title,
    ...(description ? { description } : {}),
    startTime: utcTimes.startTime,
    endTime: utcTimes.endTime,
    appointmentType: appointmentDraft.appointmentType,
    location: appointmentDraft.appointmentType === 'offline' ? (appointmentDraft.location ?? null) : null,
    meetingLink: null,
    additionalAttendees: [],
  });
};

const runSelectedInviteActions = async (
  clientId: string,
  options: {
    questionnaireIds: string[];
    appointmentDraft: ScheduleAppointmentDraftPayload | null;
  }
) => {
  const normalizedQuestionnaireIds = uniqueIds(options.questionnaireIds);
  const requests: Promise<unknown>[] = [];

  requests.push(
    ...normalizedQuestionnaireIds.map((questionnaireId) =>
      assignQuestionnaire(questionnaireId, {
        clients: [{ clientId, sendEmail: true }],
      })
    )
  );

  if (options.appointmentDraft) {
    requests.push(createOnboardingAppointment(clientId, options.appointmentDraft));
  }

  await Promise.all(requests);
};

const AddClientPage: React.FC<AddClientPageProps> = ({ adminInviteMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as AddClientPageLocationState | null) ?? null;
  const resumeDraftClientId =
    locationState?.draftClientId ??
    new URLSearchParams(location.search).get('draftClientId')?.trim() ??
    undefined;
  const onboardingDraftStorageKey = useMemo(
    () => getAddClientOnboardingDraftStorageKey(adminInviteMode),
    [adminInviteMode]
  );
  const [restoredOnboardingDraft, setRestoredOnboardingDraft] = useState<AddClientOnboardingDraft | null>(null);

  const returnTo = locationState?.returnTo ?? (adminInviteMode ? '/clients?status=active' : '/clients');

  const [resolvedInitialValues, setResolvedInitialValues] = useState<Partial<FormData> | undefined>(
    resumeDraftClientId ? undefined : locationState?.initialValues
  );
  /** Server-persisted draft (Save as Draft). Set once a draft is saved or when resuming one from the Drafts tab. */
  const [draftClientId, setDraftClientId] = useState<string | null>(resumeDraftClientId ?? null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftPersistenceError, setDraftPersistenceError] = useState<DraftPersistenceErrorState | null>(null);
  const [isLoadingDraft, setIsLoadingDraft] = useState(Boolean(resumeDraftClientId));
  const [isInviteOnlyMode, setIsInviteOnlyMode] = useState(false);
  /** Where invite UI was opened from — drives which POST /clients shape runs on send. */
  const [inviteFlowSource, setInviteFlowSource] = useState<AddClientInviteFlowSource | null>(null);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteActions, setInviteActions] = useState<InviteActionsState>(() =>
    ({
      scheduleAppointment: false,
      attachQuestionnaire: false,
      requestPayment: false,
    })
  );
  const [isQuestionnaireModalOpen, setIsQuestionnaireModalOpen] = useState(false);
  const [selectedQuestionnaireIds, setSelectedQuestionnaireIds] = useState<string[]>([]);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [appointmentPayload, setAppointmentPayload] = useState<ScheduleAppointmentDraftPayload | null>(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [pendingInvoiceDraft, setPendingInvoiceDraft] = useState<CreateInvoicePayload | null>(null);
  const [pendingInvoiceFormDraft, setPendingInvoiceFormDraft] = useState<InvoiceFormDraft | null>(null);
  const [applyGrowthFactor, setApplyGrowthFactor] = useState(true);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [createdClientId, setCreatedClientId] = useState<string | null>(null);

  const {
    formData,
    currentStep,
    setCurrentStep,
    isSaving,
    setIsSaving,
    step1Form,
    step2Form,
    handleInputChange,
    handlePhoneChange,
    resetForm,
    phoneCountry,
    setPhoneCountry,
    setFormData,
  } = useClientForm({ excludeClientId: draftClientId ?? undefined });

  /** Build one submit snapshot without letting inactive form defaults overwrite filled fields. */
  const getSubmitFormData = useCallback((): FormData => {
    const step1Fields = pickSubmitFields(
      formData,
      step1Form.getValues() as Partial<FormData>,
      STEP1_SUBMIT_FIELDS
    );

    if (inviteFlowSource !== 'step2_full') {
      return {
        ...formData,
        ...step1Fields,
      };
    }
    const step2Fields = pickSubmitFields(
      formData,
      step2Form.getValues() as Partial<FormData>,
      STEP2_SUBMIT_FIELDS
    );
    return {
      ...formData,
      ...step2Fields,
      ...step1Fields,
    };
  }, [formData, inviteFlowSource, step1Form, step2Form]);

  /** Snapshot for Save as Draft — includes step 2 fields once the user has reached step 2, regardless of invite flow. */
  const getDraftFormSnapshot = useCallback((): FormData => {
    const step1Fields = pickDraftFields(
      formData,
      step1Form.getValues() as Partial<FormData>,
      STEP1_SUBMIT_FIELDS
    );

    if (currentStep < 2 && !isInviteOnlyMode) {
      return {
        ...INITIAL_FORM_DATA,
        ...formData,
        ...step1Fields,
      };
    }
    const step2Fields = pickDraftFields(
      formData,
      step2Form.getValues() as Partial<FormData>,
      STEP2_SUBMIT_FIELDS
    );
    return {
      ...INITIAL_FORM_DATA,
      ...formData,
      ...step1Fields,
      ...step2Fields,
    };
  }, [currentStep, formData, isInviteOnlyMode, step1Form, step2Form]);

  const appliedInitialRef = useRef(false);
  const pendingStep2FormulaFetchRef = useRef(false);

  useEffect(() => {
    appliedInitialRef.current = false;
    if (resumeDraftClientId) {
      // Hydrated from the server draft fetch effect below instead of sessionStorage.
      setRestoredOnboardingDraft(null);
      setResolvedInitialValues(undefined);
      return;
    }
    removeAddClientOnboardingDraft(onboardingDraftStorageKey);
    setRestoredOnboardingDraft(null);
    setResolvedInitialValues(locationState?.initialValues);
  }, [location.key, locationState?.initialValues, onboardingDraftStorageKey, resumeDraftClientId]);

  // Resume a server-persisted draft (from the Drafts tab): fetch its onboarding snapshot and
  // hydrate the wizard the same way a local sessionStorage draft would.
  useEffect(() => {
    if (!resumeDraftClientId) return;
    let cancelled = false;
    setIsLoadingDraft(true);

    getClientDraft(resumeDraftClientId)
      .then(({ client }) => {
        if (cancelled) return;
        setDraftPersistenceError(null);
        setDraftClientId(client.id);

        const saved = client.onboardingDraft;
        if (!saved) return;

        const rawStep = Number(saved.currentStep) || 1;
        const flowSource: AddClientInviteFlowSource | null =
          saved.inviteFlowSource === 'step1_quick' || saved.inviteFlowSource === 'step2_full'
            ? saved.inviteFlowSource
            : null;
        const isInviteStep = saved.isInviteOnlyMode === true || rawStep >= 3;
        const localCurrentStep = isInviteStep
          ? (flowSource === 'step2_full' ? 2 : 1)
          : Math.min(Math.max(Math.trunc(rawStep), 1), 2);

        const converted: AddClientOnboardingDraft = {
          version: ADD_CLIENT_ONBOARDING_DRAFT_VERSION,
          formData: (saved.formData ?? {}) as Partial<FormData>,
          currentStep: localCurrentStep,
          isInviteOnlyMode: isInviteStep,
          inviteFlowSource: flowSource,
          inviteMessage: typeof saved.inviteMessage === 'string' ? saved.inviteMessage : '',
          inviteActions: normalizeInviteActions(saved.inviteActions),
          selectedQuestionnaireIds: Array.isArray(saved.selectedQuestionnaireIds)
            ? saved.selectedQuestionnaireIds.map((id) => String(id)).filter(Boolean)
            : [],
          appointmentPayload: (saved.appointmentPayload ?? null) as ScheduleAppointmentDraftPayload | null,
          pendingInvoiceDraft: (saved.pendingInvoiceDraft ?? null) as CreateInvoicePayload | null,
          pendingInvoiceFormDraft: (saved.pendingInvoiceFormDraft ?? null) as InvoiceFormDraft | null,
          applyGrowthFactor: saved.applyGrowthFactor !== false,
          createdClientId: null,
          updatedAt: Date.now(),
        };

        setRestoredOnboardingDraft(converted);
        setResolvedInitialValues(converted.formData);
      })
      .catch((error) => {
        if (!cancelled) {
          const draftError = getDraftPersistenceErrorState(error, 'Unable to load draft');
          setDraftPersistenceError(draftError);
          showErrorToast(new Error(draftError.message), draftError.title);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDraft(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resumeDraftClientId]);

  useEffect(() => {
    if (appliedInitialRef.current) return;
    if (!resolvedInitialValues) return;

    appliedInitialRef.current = true;
    const merged = { ...INITIAL_FORM_DATA, ...resolvedInitialValues };

    setFormData(merged);
    step1Form.reset(merged);
    step2Form.reset(merged);
    step1Form.setValue('gender', merged.gender ?? '', { shouldValidate: false });
    step1Form.setValue('sexAtBirth', merged.sexAtBirth ?? '', { shouldValidate: false });
    step1Form.setValue('dateOfBirth', merged.dateOfBirth ?? '', { shouldValidate: false });
    step1Form.setValue('age', merged.age ?? '', { shouldValidate: false });
    step1Form.setValue('phone', merged.phone ?? '', { shouldValidate: false });
    step1Form.setValue('phone_country_code', merged.phone_country_code ?? '', { shouldValidate: false });

    const locationCountryCode = resolvedInitialValues.location
      ? getPhoneCountryFromLocation(String(resolvedInitialValues.location))
      : undefined;

    if (locationCountryCode) {
      setPhoneCountry(locationCountryCode);
    } else if (merged.phone_country_code) {
      setPhoneCountry(getCountryCodeFromDialCode(String(merged.phone_country_code)));
    }

    if (restoredOnboardingDraft && !locationState?.initialValues) {
      setCurrentStep(restoredOnboardingDraft.currentStep);
      setIsInviteOnlyMode(restoredOnboardingDraft.isInviteOnlyMode);
      setInviteFlowSource(restoredOnboardingDraft.inviteFlowSource);
      setInviteMessage(restoredOnboardingDraft.inviteMessage);
      setInviteActions(restoredOnboardingDraft.inviteActions);
      setSelectedQuestionnaireIds(restoredOnboardingDraft.selectedQuestionnaireIds);
      setAppointmentPayload(restoredOnboardingDraft.appointmentPayload);
      setPendingInvoiceDraft(restoredOnboardingDraft.pendingInvoiceDraft);
      setPendingInvoiceFormDraft(restoredOnboardingDraft.pendingInvoiceFormDraft);
      setApplyGrowthFactor(restoredOnboardingDraft.applyGrowthFactor);
      setCreatedClientId(restoredOnboardingDraft.createdClientId);
      if (restoredOnboardingDraft.currentStep >= 2) {
        pendingStep2FormulaFetchRef.current = true;
      }
    }
  }, [
    locationState?.initialValues,
    resolvedInitialValues,
    restoredOnboardingDraft,
    setCurrentStep,
    setFormData,
    setPhoneCountry,
    step1Form,
    step2Form,
  ]);

  const biologicalSexForCalculations = useMemo(() => {
    if (formData.gender === 'male' || formData.gender === 'female') {
      return formData.gender;
    }
    if (formData.sexAtBirth) {
      return formData.sexAtBirth;
    }
    return '';
  }, [formData.gender, formData.sexAtBirth]);

  const {
    allFormulas,
    viableFormulas,
    isLoadingFormulas,
    defaultFormulaId,
    drvGuidelines,
    isLoadingDrvGuidelines,
    defaultDRVGuidelineId,
    allowedActivityLevels,
    fetchFormulas,
    reset: resetFormulas,
  } = useFormulas({
    dateOfBirth: formData.dateOfBirth,
    gender: biologicalSexForCalculations,
    location: formData.location,
    currentStep,
  });

  const {
    calculationResult,
    isCalculating,
    calculationError,
    validationWarnings,
    reset: resetCalculation,
  } = useCalculation({
    currentStep,
    formData,
    applyGrowthFactor,
  });

  const minimumEnergyWarning = useMemo(() => {
    if (currentStep !== 2) return null;

    const minimumRequired = getMinimumEnergyThreshold(biologicalSexForCalculations);
    if (minimumRequired === null) return null;

    const backendWarning = validationWarnings.find(
      (warning) => warning.warningType === 'minimum_energy_threshold'
    );
    const backendActualValue = toFiniteNumber(backendWarning?.actualValue);
    const calculatedEer = toFiniteNumber(calculationResult?.eer);
    const customAdjustment = parseAdjustmentValue(formData.customCalorieAdjustment);
    const calculationAdjustment = toFiniteNumber(calculationResult?.calculationDetails?.weightGoalAdjustment);
    const fallbackAdjustment = WEIGHT_GOAL_ADJUSTMENT_BY_VALUE[normalizeGoalKey(formData.weightLossGoal)] ?? 0;
    const weightGoalAdjustment = customAdjustment ?? calculationAdjustment ?? fallbackAdjustment;
    const calculatedActualValue = calculatedEer === null ? null : calculatedEer + weightGoalAdjustment;
    const actualValue = backendActualValue ?? calculatedActualValue;

    if (actualValue === null || actualValue >= minimumRequired) {
      return null;
    }

    return {
      actualValue,
      minimumRequired,
      sexMinimumLabel: getSexMinimumLabel(biologicalSexForCalculations),
    };
  }, [
    biologicalSexForCalculations,
    calculationResult?.calculationDetails?.weightGoalAdjustment,
    calculationResult?.eer,
    currentStep,
    formData.customCalorieAdjustment,
    formData.weightLossGoal,
    validationWarnings,
  ]);

  const {
    showFormulaValidationModal,
    nonViableFormulaName,
    currentFormulaDisplayName,
    handleNonViableFormulaSelected,
    handleFormulaValidationConfirm,
    setShowModal,
    reset: resetFormulaValidation,
  } = useFormulaWarningModal({
    formData,
    allFormulas,
    defaultFormulaId,
    viableFormulas,
  });

  useDefaultValues({
    currentStep,
    formData,
    allFormulas,
    viableFormulas,
    defaultFormulaId,
    drvGuidelines,
    defaultDRVGuidelineId,
    allowedActivityLevels,
    handleInputChange,
  });

  const pregnancyLactationValidationErrors = useMemo(
    () => getPregnancyLactationValidationErrors(formData),
    [formData]
  );
  const isPregnancyLactationBlocked = pregnancyLactationValidationErrors.length > 0;
  const hasStep1Errors = Object.keys(step1Form.formState.errors).length > 0;
  const isStep1SnapshotValid = step1Schema.safeParse({
    ...INITIAL_FORM_DATA,
    ...formData,
    ...(step1Form.getValues() as Partial<FormData>),
  }).success;
  const isStep1ActionDisabled =
    isSaving ||
    step1Form.formState.isSubmitting ||
    step1Form.formState.isValidating ||
    hasStep1Errors ||
    !isStep1SnapshotValid;

  // When a saved draft restores the wizard straight into step 2 / Finish (e.g.
  // after a reload), the formula and guideline data that is normally loaded
  // during the step 1 → step 2 transition is missing. Fetch it once the
  // restored profile fields (DOB + biological sex) have been applied.
  useEffect(() => {
    if (!pendingStep2FormulaFetchRef.current) return;
    if (currentStep < 2) return;
    if (!formData.dateOfBirth || !biologicalSexForCalculations) return;
    pendingStep2FormulaFetchRef.current = false;
    void fetchFormulas();
  }, [currentStep, formData.dateOfBirth, biologicalSexForCalculations, fetchFormulas]);

  useEffect(() => {
    return () => {
      resetFormulas();
      resetCalculation();
      resetFormulaValidation();
    };
  }, [resetCalculation, resetFormulaValidation, resetFormulas]);

  const clearOnboardingDraft = useCallback(() => {
    removeAddClientOnboardingDraft(onboardingDraftStorageKey);
    setCreatedClientId(null);
  }, [onboardingDraftStorageKey]);

  const onboardingMode = adminInviteMode ? 'admin_invite' : 'client_add';
  const onboardingStepName = useMemo<ClientOnboardingStepName>(() => {
    if (isInviteOnlyMode) {
      return inviteFlowSource === 'step1_quick' ? 'quick_invite_review' : 'full_invite_review';
    }

    return currentStep === 1 ? 'basic_information' : 'health_measurements';
  }, [currentStep, inviteFlowSource, isInviteOnlyMode]);
  const lastTrackedOnboardingStepRef = useRef('');
  const onboardingFlowStartedAtRef = useRef(Date.now());
  const onboardingStepEnteredAtRef = useRef(Date.now());
  const activeOnboardingStepRef = useRef<ClientOnboardingStepName>(onboardingStepName);

  const getOnboardingTimingProperties = () => {
    const now = Date.now();
    return {
      onboarding_flow_elapsed_ms: now - onboardingFlowStartedAtRef.current,
      onboarding_step_time_spent_ms: now - onboardingStepEnteredAtRef.current,
    };
  };

  const getOnboardingCommonProperties = () => ({
    invite_flow_source: inviteFlowSource ?? undefined,
    onboarding_mode: onboardingMode,
    onboarding_step: onboardingStepName,
    product_area: 'client_onboarding',
  });

  const getCurrentStepButtonLabel = (buttonName: string) => `${buttonName}_step_${isInviteOnlyMode ? 3 : currentStep}`;

  const getSpecificStepCompletedEventName = (transition: string) => {
    if (transition === 'health_measurements_to_full_invite_review') {
      return 'Client Onboarding Step 2 Completed' as const;
    }

    if (transition.endsWith('_to_post_completion_destination')) {
      return 'Client Onboarding Step 3 Completed' as const;
    }

    return null;
  };

  const trackOnboardingStepCompleted = (
    nextStep: string,
    transition: string,
    extraProperties: Record<string, string | number | boolean | undefined> = {}
  ) => {
    const properties = {
      ...getOnboardingCommonProperties(),
      ...getOnboardingTimingProperties(),
      ...extraProperties,
      onboarding_next_step: nextStep,
      onboarding_transition: transition,
    };

    trackClientOnboardingStepCompleted(properties, getSpecificStepCompletedEventName(transition));
  };

  const trackInviteFollowUpSelection = (
    action: keyof InviteActionsState,
    selected: boolean,
    extraProperties: Record<string, string | number | boolean | undefined> = {}
  ) => {
    trackClientOnboardingFollowUpSelected({
      ...getOnboardingCommonProperties(),
      ...extraProperties,
      follow_up_action: normalizeInviteActionName(action),
      follow_up_selected: selected,
    });
  };

  useEffect(() => {
    const stepKey = `${onboardingMode}|${onboardingStepName}|${inviteFlowSource ?? 'manual'}`;
    if (lastTrackedOnboardingStepRef.current === stepKey) {
      return;
    }

    const now = Date.now();
    const previousStep = activeOnboardingStepRef.current;
    const previousStepTimeSpentMs =
      previousStep === onboardingStepName ? undefined : now - onboardingStepEnteredAtRef.current;

    lastTrackedOnboardingStepRef.current = stepKey;
    trackClientOnboardingStepViewed({
      onboarding_flow_elapsed_ms: now - onboardingFlowStartedAtRef.current,
      has_appointment_draft: Boolean(appointmentPayload),
      has_invoice_draft: Boolean(pendingInvoiceDraft),
      has_selected_questionnaires: selectedQuestionnaireIds.length > 0,
      invite_flow_source: inviteFlowSource ?? undefined,
      onboarding_previous_step: previousStep === onboardingStepName ? undefined : previousStep,
      onboarding_previous_step_time_spent_ms: previousStepTimeSpentMs,
      onboarding_mode: onboardingMode,
      onboarding_step: onboardingStepName,
      product_area: 'client_onboarding',
      questionnaire_count: selectedQuestionnaireIds.length,
    });

    activeOnboardingStepRef.current = onboardingStepName;
    onboardingStepEnteredAtRef.current = now;
  }, [
    appointmentPayload,
    inviteFlowSource,
    onboardingMode,
    onboardingStepName,
    pendingInvoiceDraft,
    selectedQuestionnaireIds.length,
  ]);

  const nextStep = async () => {
    if (currentStep === 1) {
      if (!formData.sexAtBirth && (formData.gender === 'male' || formData.gender === 'female')) {
        step1Form.setValue('sexAtBirth', formData.gender, { shouldValidate: false });
        handleInputChange('sexAtBirth', formData.gender);
      }
      step1Form.handleSubmit(async () => {
        await fetchFormulas();
        trackOnboardingStepCompleted('health_measurements', 'basic_information_to_health_measurements', {
          clicked_button: 'continue_step_1_add_manually',
          onboarding_action: 'continue_to_add_manually',
        });
        setIsInviteOnlyMode(false);
        setCurrentStep(2);
      })();
      return;
    }

    step2Form.setValue('gender', formData.gender || '');
    step2Form.setValue('sexAtBirth', formData.sexAtBirth || '');
    step2Form.setValue('dateOfBirth', formData.dateOfBirth || '');
    step2Form.setValue('pregnancyStatus', formData.pregnancyStatus || '');
    step2Form.setValue('pregnancyTrimester', formData.pregnancyTrimester || '');
    step2Form.setValue('prePregnancyWeightKg', formData.prePregnancyWeightKg || '');
    step2Form.setValue('lactationStatus', formData.lactationStatus || '');
    step2Form.setValue('twinPregnancy', formData.twinPregnancy || '');
    step2Form.setValue('weightLossGoal', formData.weightLossGoal || '');
    step2Form.setValue('customCalorieAdjustment', formData.customCalorieAdjustment || '');
    step2Form.setValue('diabetesType', formData.diabetesType || '');
    step2Form.setValue('thyroidType', formData.thyroidType || '');

    step2Form.clearErrors(['pregnancyTrimester', 'prePregnancyWeightKg', 'twinPregnancy', 'lactationStatus', 'weightLossGoal']);
    if (pregnancyLactationValidationErrors.length > 0) {
      pregnancyLactationValidationErrors.forEach(({ field, message }) => {
        step2Form.setError(field, {
          type: 'manual',
          message,
        });
      });
      return;
    }

    const isValid = await step2Form.trigger();
    if (!isValid) {
      return;
    }

    trackOnboardingStepCompleted('full_invite_review', 'health_measurements_to_full_invite_review', {
      clicked_button: 'continue_step_2',
      onboarding_action: 'continue_to_review',
    });
    setInviteFlowSource('step2_full');
    setCreatedClientId(null);
    setIsInviteOnlyMode(true);
  };

  const prevStep = () => {
    const nextStepName = currentStep <= 2 ? 'basic_information' : 'health_measurements';
    trackClientOnboardingBackClicked({
      ...getOnboardingCommonProperties(),
      ...getOnboardingTimingProperties(),
      onboarding_back_action: 'previous_step',
      clicked_button: getCurrentStepButtonLabel('go_back'),
      onboarding_next_step: nextStepName,
      onboarding_transition: `${onboardingStepName}_to_${nextStepName}`,
    });
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const exitWizard = () => {
    trackClientOnboardingExited({
      ...getOnboardingCommonProperties(),
      ...getOnboardingTimingProperties(),
      clicked_button: getCurrentStepButtonLabel('go_back'),
      onboarding_exit_action: 'exit_wizard',
      onboarding_next_route: normalizeOnboardingRoute(returnTo),
      onboarding_next_surface: getOnboardingDestinationSurface(returnTo),
    });
    clearOnboardingDraft();
    navigate(returnTo, { replace: true });
  };

  const leaveInviteMode = () => {
    const nextStepName: ClientOnboardingStepName =
      inviteFlowSource === 'step2_full' ? 'health_measurements' : 'basic_information';
    trackClientOnboardingBackClicked({
      ...getOnboardingCommonProperties(),
      ...getOnboardingTimingProperties(),
      onboarding_back_action: 'leave_review',
      clicked_button: 'go_back_step_3',
      onboarding_next_step: nextStepName,
      onboarding_transition: `${onboardingStepName}_to_${nextStepName}`,
    });
    clearOnboardingDraft();
    setIsInviteOnlyMode(false);
    setInviteFlowSource(null);
  };

  const openInviteOnlyFlow = () => {
    if (isSaving) return;

    step1Form.handleSubmit(() => {
      trackOnboardingStepCompleted('quick_invite_review', 'basic_information_to_quick_invite_review', {
        clicked_button: 'invite_client_step_1',
        onboarding_action: 'quick_invite',
      });
      setInviteMessage('');
      setInviteActions({
        scheduleAppointment: false,
        attachQuestionnaire: false,
        requestPayment: false,
      });
      setAppointmentPayload(null);
      setSelectedQuestionnaireIds([]);
      setPendingInvoiceDraft(null);
      setPendingInvoiceFormDraft(null);
      setCreatedClientId(null);
      setInviteFlowSource('step1_quick');
      setIsInviteOnlyMode(true);
    })();
  };

  const handleInviteActionChange = (action: keyof InviteActionsState, checked: boolean) => {
    trackInviteFollowUpSelection(action, checked);

    setInviteActions((prev) => ({
      ...prev,
      [action]: checked,
    }));

    if (action === 'scheduleAppointment' && !checked) {
      setAppointmentPayload(null);
    }

    if (action === 'attachQuestionnaire' && !checked) {
      setSelectedQuestionnaireIds([]);
    }

    if (action === 'requestPayment' && !checked) {
      setPendingInvoiceDraft(null);
      setPendingInvoiceFormDraft(null);
    }
  };

  const handleScheduleAppointmentClick = () => {
    if (isAddingClient) return;
    setIsAppointmentModalOpen(true);
  };

  const handleAppointmentSchedule = (payload: ScheduleAppointmentDraftPayload) => {
    setAppointmentPayload(payload);
    setInviteActions((prev) => ({
      ...prev,
      scheduleAppointment: true,
    }));
    trackInviteFollowUpSelection('scheduleAppointment', true, {
      appointment_draft_saved: true,
    });
    setIsAppointmentModalOpen(false);
  };

  const inviteClientDisplayName = useMemo(() => {
    const name = [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim();
    return name || 'Client';
  }, [formData.firstName, formData.lastName]);

  const appointmentPrefill = useMemo(() => {
    if (!appointmentPayload) return undefined;
    const parts = appointmentPayload.date.split('-').map((value) => Number(value));
    const date =
      parts.length === 3 && parts.every((value) => Number.isFinite(value))
        ? new Date(parts[0], parts[1] - 1, parts[2])
        : undefined;
    return {
      date,
      time: appointmentPayload.startTime,
      durationMinutes: appointmentPayload.durationMinutes,
    };
  }, [appointmentPayload]);

  const handleAttachQuestionnaireClick = () => {
    if (isAddingClient) return;
    setIsQuestionnaireModalOpen(true);
  };

  const handleRequestPaymentClick = () => {
    if (isAddingClient) return;
    setIsInvoiceModalOpen(true);
  };

  const inviteClientSnapshot = useMemo<Partial<Client>>(
    () => ({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      fullName: `${formData.firstName ?? ''} ${formData.lastName ?? ''}`.trim(),
    }),
    [formData.email, formData.firstName, formData.lastName]
  );

  const handleAttachQuestionnaires = (questionnaireIds: string[]) => {
    const normalizedQuestionnaireIds = uniqueIds(questionnaireIds);
    setSelectedQuestionnaireIds(normalizedQuestionnaireIds);
    setInviteActions((prev) => ({
      ...prev,
      attachQuestionnaire: normalizedQuestionnaireIds.length > 0,
    }));
    trackInviteFollowUpSelection('attachQuestionnaire', normalizedQuestionnaireIds.length > 0, {
      questionnaire_count: normalizedQuestionnaireIds.length,
    });
    setIsQuestionnaireModalOpen(false);
  };

  const handleInvoiceDraftSave = (payload: CreateInvoicePayload) => {
    setPendingInvoiceDraft(payload);
    setInviteActions((prev) => ({
      ...prev,
      requestPayment: true,
    }));
    trackInviteFollowUpSelection('requestPayment', true, {
      invoice_draft_saved: true,
    });
  };

  const validateDraftPhone = async (snapshot: FormData) => {
    step1Form.setValue('phone', snapshot.phone ?? '', { shouldValidate: false });
    step1Form.setValue('phone_country_code', snapshot.phone_country_code ?? '', { shouldValidate: false });

    const isPhoneValid = await step1Form.trigger(['phone', 'phone_country_code']);
    if (!isPhoneValid) {
      setIsInviteOnlyMode(false);
      setCurrentStep(1);
      showErrorToast(new Error('Enter a complete phone number before saving this draft.'), 'Phone number incomplete');
    }
    return isPhoneValid;
  };

  /** Saves current onboarding progress as a draft — available from any step. Does not invite/activate the client. */
  const handleSaveAsDraft = async () => {
    if (isSavingDraft || isAddingClient || isSaving) return;

    setIsSavingDraft(true);
    setDraftPersistenceError(null);
    try {
      const snapshot = getDraftFormSnapshot();
      const isDraftPhoneValid = await validateDraftPhone(snapshot);
      if (!isDraftPhoneValid) return;

      const payload: SaveClientDraftPayload = {
        draftClientId: draftClientId ?? undefined,
        currentStep: isInviteOnlyMode ? 3 : currentStep,
        isInviteOnlyMode,
        inviteFlowSource,
        inviteMessage,
        inviteActions,
        selectedQuestionnaireIds,
        applyGrowthFactor,
        formData: snapshot as unknown as Record<string, unknown>,
        appointmentPayload,
        pendingInvoiceDraft,
        pendingInvoiceFormDraft,
        email: snapshot.email,
        firstName: snapshot.firstName,
        lastName: snapshot.lastName,
        phone: snapshot.phone,
        phoneCountryCode: snapshot.phone_country_code,
        location: snapshot.location,
        cityOrCounty: snapshot.cityOrCounty,
        zipCode: snapshot.zipCode,
        dateOfBirth: snapshot.dateOfBirth,
        gender: snapshot.gender,
      };

      const result = await saveOrUpdateClientDraft(payload);
      setDraftClientId(result.client.id);
      clearOnboardingDraft();

      showSuccessToast(
        'Draft saved',
        `${result.client.fullName?.trim() || 'Client'} saved as a draft. Resume anytime from the Drafts tab.`
      );
      navigate('/clients?status=draft', { replace: true });
    } catch (error) {
      const draftError = getDraftPersistenceErrorState(error, 'Unable to save draft');
      setDraftPersistenceError(draftError);
      if (getApiErrorCode(error) === 'CLIENT_DRAFT_NOT_FOUND') {
        setDraftClientId(null);
      }
      showErrorToast(new Error(draftError.message), draftError.title);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleDraftPersistenceErrorAction = useCallback(() => {
    if (!draftPersistenceError?.action) return;

    if (draftPersistenceError.action === 'view_client' && draftClientId) {
      navigate(getClientDetailsPath(draftClientId));
      return;
    }

    navigate('/clients?status=draft', { replace: true });
  }, [draftClientId, draftPersistenceError?.action, navigate]);

  /** Finalizes invite onboarding from the invite / review step. */
  const handleAddClient = async () => {
    if (isAddingClient) return;

    const afterFullHealth = inviteFlowSource === 'step2_full';
    const questionnaireIds = inviteActions.attachQuestionnaire ? uniqueIds(selectedQuestionnaireIds) : [];
    const appointmentDraft = inviteActions.scheduleAppointment ? appointmentPayload : null;
    const isPaymentRequested = inviteActions.requestPayment;

    if (inviteActions.attachQuestionnaire && questionnaireIds.length === 0) {
      showErrorToast(
        new Error('Please select at least one questionnaire before adding the client.'),
        'Questionnaire required'
      );
      setIsQuestionnaireModalOpen(true);
      return;
    }

    if (inviteActions.scheduleAppointment && !appointmentDraft) {
      showErrorToast(
        new Error('Please save the appointment details before adding the client.'),
        'Appointment required'
      );
      setIsAppointmentModalOpen(true);
      return;
    }

    if (isPaymentRequested && !pendingInvoiceDraft) {
      showErrorToast(
        new Error('Please save the payment request details before adding the client.'),
        'Payment request required'
      );
      setIsInvoiceModalOpen(true);
      return;
    }

    const hasFollowUpActions = isPaymentRequested || questionnaireIds.length > 0 || Boolean(appointmentDraft);
    let activeClientId = createdClientId;

    if (!activeClientId) {
      const saveResult = await saveClient(
        getSubmitFormData(),
        setIsAddingClient,
        (_clientData, _refresh) => undefined,
        () => undefined,
        afterFullHealth
          ? {
              sendInviteToApp: true,
              sendPaymentRequest: false,
              inviteNotes: inviteMessage,
              applyGrowthFactor,
              draftClientId: draftClientId ?? undefined,
            }
          : {
              intakeMode: 'quick',
              sendInviteToApp: true,
              sendPaymentRequest: false,
              inviteNotes: inviteMessage,
              applyGrowthFactor,
              draftClientId: draftClientId ?? undefined,
            }
      );

      if (!saveResult.success) return;
      activeClientId = saveResult.clientId ?? null;
      setCreatedClientId(activeClientId);
    }

    if (hasFollowUpActions) {
      if (!activeClientId) {
        showErrorToast(
          new Error('Client was created but no id was returned for selected actions.'),
          'Unable to finish client setup'
        );
        return;
      }

      setIsAddingClient(true);
      try {
        const followUpRequests: Promise<unknown>[] = [];

        if (isPaymentRequested && pendingInvoiceDraft) {
          followUpRequests.push(
            createClientInvoice({
              ...pendingInvoiceDraft,
              clientId: activeClientId,
            })
          );
        }

        if (questionnaireIds.length > 0 || appointmentDraft) {
          followUpRequests.push(
            runSelectedInviteActions(activeClientId, {
              questionnaireIds,
              appointmentDraft,
            })
          );
        }

        await Promise.all(followUpRequests);
      } catch (error) {
        showErrorToast(error, 'Unable to finish client setup');
        return;
      } finally {
        setIsAddingClient(false);
      }
    }

    const nextRoute = activeClientId ? getClientDetailsPath(activeClientId) : returnTo;
    const nextRoutePattern = normalizeOnboardingRoute(nextRoute);
    const nextSurface = getOnboardingDestinationSurface(nextRoute);

    trackOnboardingStepCompleted('post_completion_destination', `${onboardingStepName}_to_post_completion_destination`, {
      clicked_button: 'create_client',
      follow_up_actions_count: Number(isPaymentRequested) + Number(questionnaireIds.length > 0) + Number(Boolean(appointmentDraft)),
      has_follow_up_actions: hasFollowUpActions,
      onboarding_completed_after_full_health: afterFullHealth,
      onboarding_action: 'finish_onboarding',
      onboarding_next_route: nextRoutePattern,
      onboarding_next_surface: nextSurface,
      invoice_requested: isPaymentRequested,
      payment_requested: isPaymentRequested,
      questionnaire_attached: questionnaireIds.length > 0,
      questionnaire_count: questionnaireIds.length,
      scheduled_appointment: Boolean(appointmentDraft),
    });

    trackClientOnboardingCompleted({
      ...getOnboardingTimingProperties(),
      clicked_button: 'create_client',
      follow_up_actions_count: Number(isPaymentRequested) + Number(questionnaireIds.length > 0) + Number(Boolean(appointmentDraft)),
      has_follow_up_actions: hasFollowUpActions,
      invite_flow_source: inviteFlowSource ?? undefined,
      invoice_requested: isPaymentRequested,
      onboarding_completed_after_full_health: afterFullHealth,
      onboarding_mode: onboardingMode,
      onboarding_next_route: nextRoutePattern,
      onboarding_next_surface: nextSurface,
      onboarding_step: onboardingStepName,
      payment_requested: isPaymentRequested,
      product_area: 'client_onboarding',
      questionnaire_attached: questionnaireIds.length > 0,
      questionnaire_count: questionnaireIds.length,
      scheduled_appointment: Boolean(appointmentDraft),
    });

    clearOnboardingDraft();
    setDraftClientId(null);
    setIsInviteOnlyMode(false);
    setInviteFlowSource(null);
    resetForm();
    navigate(nextRoute, { replace: true });
  };

  const pageTitle = adminInviteMode
    ? isInviteOnlyMode
      ? 'Review Client Invite'
      : 'Invite Client'
    : isInviteOnlyMode
      ? 'Add client'
      : 'Add New Client';
  const breadcrumbLabel = 'Add Client';
  const breadcrumbItems = adminInviteMode
    ? [
        { label: 'Admin', path: '/admin/users' },
        { label: 'Client Invites' },
      ]
    : [
        { label: 'My Clients', path: '/clients' },
        { label: breadcrumbLabel },
      ];
  const primaryInviteSubmitLabel = adminInviteMode ? 'Send invite' : 'Add client';
  const loadingInviteSubmitLabel = adminInviteMode ? 'Sending invite...' : 'Adding client...';
  const onboardingHeaderStep = isInviteOnlyMode ? 3 : currentStep;

  const canUseDemoPersonas = useMemo(
    () => !adminInviteMode && isCalorieScienceEmail(getCurrentUser()?.email),
    [adminInviteMode]
  );

  const applyDemoPersona = useCallback(
    (personaId: DemoPersonaId) => {
      const persona = DEMO_PERSONAS.find((entry) => entry.id === personaId);
      if (!persona) return;

      const merged = buildDemoPersonaFormData(personaId);
      setFormData(merged);
      step1Form.reset(merged);
      step2Form.reset(merged);
      step1Form.clearErrors();
      step2Form.clearErrors();

      const locationCountryCode = merged.location
        ? getPhoneCountryFromLocation(String(merged.location))
        : undefined;
      if (locationCountryCode) {
        setPhoneCountry(locationCountryCode);
      } else if (merged.phone_country_code) {
        setPhoneCountry(getCountryCodeFromDialCode(String(merged.phone_country_code)));
      }

      showSuccessToast(
        'Demo persona loaded',
        `${persona.label} filled with a unique email and phone. Review and continue.`
      );
    },
    [setFormData, setPhoneCountry, step1Form, step2Form]
  );

  return (
    <Box data-demo-id="add-client-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <div className="p-4 sm:p-5 lg:px-7 lg:pt-6 lg:pb-7">
    {isLoadingDraft ? (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-current border-t-transparent text-ds-secondary" />
      </div>
    ) : (
    <>
      <FlowPageLayout
        title={pageTitle}
        breadcrumbItems={breadcrumbItems}
        headerExtra={!adminInviteMode ? <StepIndicator currentStep={onboardingHeaderStep} /> : undefined}
      >
        {draftPersistenceError && (
          <Alert className="mb-4 w-full max-w-none border-red-300 bg-red-50 text-left text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
            <AlertDescription className="flex flex-col gap-3 text-sm leading-5 sm:flex-row sm:items-center sm:justify-between">
              <span>{draftPersistenceError.message}</span>
              {draftPersistenceError.action && (
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  className="border-red-300 text-red-900 hover:bg-red-100 dark:border-red-700 dark:text-red-100 dark:hover:bg-red-900/40"
                  onClick={handleDraftPersistenceErrorAction}
                >
                  {draftPersistenceError.action === 'view_client' ? 'Open Client' : 'Back to Drafts'}
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {isInviteOnlyMode ? (
              <InviteOnlyStep
                clientFirstName={formData.firstName}
                clientEmail={formData.email}
                inviteMessage={inviteMessage}
                onInviteMessageChange={setInviteMessage}
                actions={inviteActions}
                onActionChange={handleInviteActionChange}
                onScheduleAppointmentClick={handleScheduleAppointmentClick}
                onAttachQuestionnaireClick={handleAttachQuestionnaireClick}
                onRequestPaymentClick={handleRequestPaymentClick}
              />
            ) : (
              <div className="space-y-5">
                {currentStep === 2 ? (
                  <div className="flex items-center justify-end gap-3">
                    {canUseDemoPersonas && (
                      <DemoPersonaPicker
                        onSelect={applyDemoPersona}
                        disabled={isAddingClient || isSaving}
                      />
                    )}
                    {/* <Button
                      data-analytics-event="Save As Draft Clicked"
                      data-analytics-id="client_onboarding_save_as_draft_button_step_2_top"
                      data-analytics-label="save_as_draft"
                      data-analytics-section="client_onboarding_step_2"
                      onClick={handleSaveAsDraft}
                      variant="outline"
                      size="small"
                      className="min-w-[160px] border-ds-secondary text-ds-secondary hover:bg-ds-surface-teal"
                      disabled={isSavingDraft || isSaving}
                    >
                      {isSavingDraft ? 'Saving Draft...' : 'Save as Draft'}
                    </Button> */}
                  </div>
                ) : canUseDemoPersonas ? (
                  <div className="flex justify-end">
                    <DemoPersonaPicker
                      onSelect={applyDemoPersona}
                      disabled={isAddingClient || isSaving}
                    />
                  </div>
                ) : null}
                {currentStep === 1 ? (
                  <BasicInformationStep
                    formData={formData}
                    step1Form={step1Form}
                    handleInputChange={handleInputChange}
                    handlePhoneChange={handlePhoneChange}
                    phoneCountry={phoneCountry}
                    initialValues={resolvedInitialValues}
                  />
                ) : (
                  <HealthMeasurementsStep
                    formData={formData}
                    step2Form={step2Form}
                    handleInputChange={handleInputChange}
                    allFormulas={allFormulas}
                    viableFormulas={viableFormulas}
                    isLoadingFormulas={isLoadingFormulas}
                    drvGuidelines={drvGuidelines}
                    isLoadingDrvGuidelines={isLoadingDrvGuidelines}
                    calculationResult={calculationResult}
                    isCalculating={isCalculating}
                    calculationError={calculationError}
                    validationWarnings={validationWarnings}
                    onNonViableFormulaSelected={handleNonViableFormulaSelected}
                    allowedActivityLevels={allowedActivityLevels}
                    onGrowthFactorChange={setApplyGrowthFactor}
                  />
                )}
              </div>
            )}

            {isInviteOnlyMode ? (
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  data-analytics-event="Go Back Step 3 Clicked"
                  data-analytics-id="client_onboarding_go_back_step_3_button"
                  data-analytics-label="go_back_step_3"
                  data-analytics-section="client_onboarding_step_3"
                  onClick={leaveInviteMode}
                  className="h-11 px-0 text-left text-sm font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={isAddingClient}
                >
                  Back
                </button>

                <Button
                  data-analytics-event="Create Client Clicked"
                  data-analytics-id="client_onboarding_create_client_button"
                  data-analytics-label="create_client"
                  data-analytics-section="client_onboarding_step_3"
                  onClick={handleAddClient}
                  variant="primary"
                  size="small"
                  disabled={isAddingClient}
                  className="min-w-[190px]"
                >
                  {isAddingClient ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      {loadingInviteSubmitLabel}
                    </>
                  ) : (
                    primaryInviteSubmitLabel
                  )}
                </Button>
              </div>
            ) : adminInviteMode ? (
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  data-analytics-event="Go Back Admin Invite Clicked"
                  data-analytics-id="client_onboarding_go_back_admin_invite_button"
                  data-analytics-label="go_back_admin_invite"
                  data-analytics-section="client_onboarding_admin_invite"
                  onClick={exitWizard}
                  className="h-11 px-0 text-left text-sm font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={isSaving}
                >
                  Back
                </button>

                <Button
                  data-analytics-event="Review Invite Clicked"
                  data-analytics-id="client_onboarding_review_invite_button"
                  data-analytics-label="review_invite"
                  data-analytics-section="client_onboarding_admin_invite"
                  onClick={openInviteOnlyFlow}
                  variant="primary"
                  size="small"
                  disabled={isSaving}
                  className="min-w-[190px]"
                >
                  Review Invite
                </Button>
              </div>
            ) : currentStep === 1 ? (
              <div className="w-full">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      data-analytics-event="Go Back Step 1 Clicked"
                      data-analytics-id="client_onboarding_go_back_step_1_button"
                      data-analytics-label="go_back_step_1"
                      data-analytics-section="client_onboarding_step_1"
                      onClick={exitWizard}
                      className="h-11 px-0 text-left text-sm font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-70"
                      disabled={isSaving}
                    >
                      Back
                    </button>
                    {/* <Button
                      data-analytics-event="Save As Draft Clicked"
                      data-analytics-id="client_onboarding_save_as_draft_button_step_1"
                      data-analytics-label="save_as_draft"
                      data-analytics-section="client_onboarding_step_1"
                      onClick={handleSaveAsDraft}
                      variant="outline"
                      size="small"
                      className="min-w-[160px] border-ds-secondary text-ds-secondary hover:bg-ds-surface-teal"
                      disabled={isSavingDraft || isSaving}
                    >
                      {isSavingDraft ? 'Saving Draft...' : 'Save as Draft'}
                    </Button> */}
                  </div>

                  <div className="flex flex-col items-end gap-3">
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Button
                        data-demo-id="add-client-continue-manual"
                        data-analytics-event="Continue Step 1 Add Manually Clicked"
                        data-analytics-id="client_onboarding_continue_step_1_add_manually_button"
                        data-analytics-label="continue_step_1_add_manually"
                        data-analytics-section="client_onboarding_step_1"
                        onClick={nextStep}
                        variant="primary"
                        size="small"
                        disabled={isStep1ActionDisabled}
                        className="min-w-[260px]"
                      >
                        Continue to Add Manually
                      </Button>
                      <Button
                        data-demo-id="add-client-invite"
                        data-analytics-event="Invite Client Step 1 Clicked"
                        data-analytics-id="client_onboarding_invite_client_step_1_button"
                        data-analytics-label="invite_client_step_1"
                        data-analytics-section="client_onboarding_step_1"
                        onClick={openInviteOnlyFlow}
                        variant="outline"
                        size="small"
                        className="min-w-[220px] border-ds-secondary text-ds-secondary hover:bg-ds-surface-teal"
                        disabled={isStep1ActionDisabled}
                      >
                        Invite Client to Fill
                      </Button>
                    </div>
                    <div className="rounded-md bg-ds-surface-blue px-4 py-2 text-sm text-ds-neutral-900 shadow-sm">
                      Send Invitation via Email
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    data-analytics-event="Go Back Step 2 Clicked"
                    data-analytics-id="client_onboarding_go_back_step_2_button"
                    data-analytics-label="go_back_step_2"
                    data-analytics-section="client_onboarding_step_2"
                    onClick={prevStep}
                    className="h-11 px-0 text-left text-sm font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-70"
                    disabled={isSaving}
                  >
                    Back
                  </button>
                  {/* <Button
                    data-analytics-event="Save As Draft Clicked"
                    data-analytics-id="client_onboarding_save_as_draft_button_step_2"
                    data-analytics-label="save_as_draft"
                    data-analytics-section="client_onboarding_step_2"
                    onClick={handleSaveAsDraft}
                    variant="outline"
                    size="small"
                    className="min-w-[160px] border-ds-secondary text-ds-secondary hover:bg-ds-surface-teal"
                    disabled={isSavingDraft || isSaving}
                  >
                    {isSavingDraft ? 'Saving Draft...' : 'Save as Draft'}
                  </Button> */}
                </div>

                <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:items-end">
                  <Button
                    data-demo-id="add-client-continue-health"
                    data-analytics-event="Continue Step 2 Clicked"
                    data-analytics-id="client_onboarding_continue_step_2_button"
                    data-analytics-label="continue_step_2"
                    data-analytics-section="client_onboarding_step_2"
                    onClick={nextStep}
                    variant="primary"
                    size="small"
                    disabled={isPregnancyLactationBlocked}
                    className="min-w-[190px]"
                  >
                    Continue
                  </Button>

                  {minimumEnergyWarning && (
                    <Alert className="w-full max-w-none border-amber-500/60 bg-amber-50 text-left text-amber-900 dark:border-amber-400/60 dark:bg-amber-900/30 dark:text-amber-50">
                      <AlertDescription className="text-sm leading-5 sm:whitespace-nowrap">
                        Energy intake must be above {Math.round(minimumEnergyWarning.minimumRequired)} kcal/day for {minimumEnergyWarning.sexMinimumLabel}.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>
            )}
      </FlowPageLayout>

      <InviteQuestionnaireModal
        open={isQuestionnaireModalOpen}
        onClose={() => setIsQuestionnaireModalOpen(false)}
        clientFirstName={formData.firstName}
        clientLastName={formData.lastName}
        clientEmail={formData.email}
        selectedQuestionnaireIds={selectedQuestionnaireIds}
        onAttach={handleAttachQuestionnaires}
      />

      <ScheduleAppointmentDialog
        open={isAppointmentModalOpen}
        onClose={() => setIsAppointmentModalOpen(false)}
        defaultTitle={appointmentPayload?.title ?? 'Consultation'}
        defaultDescription={appointmentPayload?.description ?? ''}
        lockedClient={{
          name: inviteClientDisplayName,
          email: formData.email,
        }}
        prefill={appointmentPrefill}
        onScheduleDraft={handleAppointmentSchedule}
      />

      <CreateInvoiceFlowDialog
        open={isInvoiceModalOpen}
        onClose={() => setIsInvoiceModalOpen(false)}
        initialClientSnapshot={inviteClientSnapshot}
        initialFormDraft={pendingInvoiceFormDraft}
        deferInvoiceCreation
        onFormDraftSave={setPendingInvoiceFormDraft}
        onInvoiceDraftSave={handleInvoiceDraftSave}
      />

      <FormulaValidationModal
        open={showFormulaValidationModal}
        onOpenChange={setShowModal}
        selectedFormulaName={nonViableFormulaName}
        fallbackFormulaName={currentFormulaDisplayName}
        onConfirm={handleFormulaValidationConfirm}
      />
    </>
    )}
        </div>
      </Paper>
    </Box>
  );
};

export default AddClientPage;
